<template>
  <div class="page-thanks page">
    <BreadcrumbsComponent @back.prevent :links="[{ title: 'Оформление' }]" />
    <div class="sides-padding">
      <div class="container">
        <div class="page-thanks__container">
          <div class="page-thanks__content">
            <span class="page-thanks__image">
              <IconComponent name="emoji-happy" />
            </span>
            <span class="page-thanks__title">
              Ваш заказ №{{ params.id }} от {{ params.date | formatDateTime }} успешно создан.
            </span>
            <span v-if="!isOnlinePayment" class="page-thanks__subtitle">
              Вы можете следить за выполнением своего заказа в
              <router-link :to="{ name: 'user', params: { link: 'orders' } }">
                Персональном разделе сайта.
              </router-link>
            </span>
          </div>
          <div class="page-thanks__footer" :class="{ 'page-thanks__footer--payment': isOnlinePayment }">
            <span class="page-thanks__footer-title">Оплата заказа</span>
            <span v-if="!isOnlinePayment" class="page-thanks__footer-subtitle">{{ orderType }}</span>

            <template v-if="isOnlinePayment">
              <p class="page-thanks__pay-notice">
                Заказ ожидает оплаты. Нажмите кнопку, чтобы завершить покупку.
              </p>
              <button
                class="btn btn--md btn--main page-thanks__pay-btn"
                :disabled="paymentLoading"
                @click="goToPayment"
              >
                <template v-if="countdown > 0">Перейти к оплате ({{ countdown }})</template>
                <template v-else-if="paymentLoading">Загрузка...</template>
                <template v-else>Перейти к оплате</template>
              </button>
              <span v-if="paymentError" class="page-thanks__pay-error">{{ paymentError }}</span>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NOT_FOUND_PAGE from "@/graphql/pages/NotFoundPage.graphql";
import OrderRepeatPayment from "@/graphql/mutations/OrderRepeatPayment.graphql";
import BreadcrumbsComponent from "components/BreadcrumbsComponent.vue";
import IconComponent from "components/IconComponent.vue";

function parseParams(query) {
  const result = { ...query };
  ["id", "type"].forEach((key) => {
    if (result[key] !== undefined) {
      try { result[key] = JSON.parse(result[key]); } catch (e) {}
    }
  });
  return result;
}

export default {
  name: "ThankYouPage",
  components: { IconComponent, BreadcrumbsComponent },
  async asyncData({ res, apollo, store }) {
    await apollo.defaultClient
      .query({ query: NOT_FOUND_PAGE })
      .then(({ data }) => {
        store.state.all_brands = data.brands || [];
        store.state.popular_brands = data.brands_paginate.data || [];
        store.state.categories = data.header_categories.data || [];
        store.state.pages = data.pages;
        store.state.marketplace = data.marketplace;
      })
      .catch(() => {});
    if (res) res.status(404);
  },
  data() {
    return {
      params: {},
      countdown: 0,
      paymentLoading: false,
      paymentError: null,
      _timer: null,
    };
  },
  created() {
    this.params = parseParams(this.$route.query);
  },
  mounted() {
    if (this.params.payment_url) {
      this.startCountdown();
    }
  },
  beforeDestroy() {
    clearInterval(this._timer);
  },
  computed: {
    types() {
      return this.$store.state._types;
    },
    isOnlinePayment() {
      return this.params.payment_url || this.params.type === this.types.ONLINE_CARD_PAY;
    },
    orderType() {
      switch (this.params.type) {
        case this.types.ONLINE_CARD_PAY: return "Оплата онлайн";
        case this.types.OFFLINE_CARD_PAY: return "Оплата картой при получении";
        case this.types.OFFLINE_CASH_PAY: return "Оплата наличными при получении";
        default: return "Не указано";
      }
    },
  },
  methods: {
    startCountdown() {
      this.countdown = 3;
      this._timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this._timer);
          this.redirect(this.params.payment_url);
        }
      }, 1000);
    },
    redirect(url) {
      // Убираем payment_url из истории чтобы при возврате назад не было повторного отсчёта
      const cleanQuery = { ...this.$route.query };
      delete cleanQuery.payment_url;
      const qs = Object.keys(cleanQuery).length ? "?" + new URLSearchParams(cleanQuery).toString() : "";
      history.replaceState(null, "", this.$route.path + qs);
      this.params.payment_url = null;
      window.location.href = url;
    },
    async goToPayment() {
      clearInterval(this._timer);
      this.countdown = 0;
      this.paymentLoading = true;
      this.paymentError = null;

      if (this.params.payment_url) {
        this.redirect(this.params.payment_url);
        return;
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: OrderRepeatPayment,
          variables: { order_id: Number(this.params.id) },
          context: { headers: { Authorization: "Bearer " + this.$store.state.apollo_token } },
        });
        const result = data && data.OrderRepeatPayment;
        if (result && result.payment_url) {
          window.location.href = result.payment_url;
        } else {
          this.paymentError = (result && result.message) || "Не удалось получить ссылку на оплату";
          this.paymentLoading = false;
        }
      } catch (e) {
        this.paymentError = "Ошибка: " + e.message;
        this.paymentLoading = false;
      }
    },
  },
  metaInfo: {
    title: "Спасибо!",
    titleTemplate: null,
  },
};
</script>

<style lang="stylus">
.page-thanks {
  display flex
  flex-direction column
  gap: 50px

  &__container {
    max-width 557px
    margin 0 auto
  }

  &__content {
    display flex
    flex-direction column
    align-items center
    gap 30px
    text-align center
    border: 1px solid var(--gray);
    border-radius: var(--main_radius) var(--main_radius) 0 0;
    padding 30px
    +below(440px) {
      padding 20px
      gap: 20px
    }
  }

  &__image {
    display flex
    padding 35px
    border: 1px solid var(--gray)
    border-radius: 50%
    margin-bottom 15px
    +below(580px) {
      margin-bottom: 0;
    }
    +below(440px) {
      padding 20px
    }

    .icon {
      width 80px
      height 80px

      svg path {
        stroke var(--main-color-75)
      }
    }
  }

  &__title {
    font-weight: 700;
    font-size: 1.375em
    line-height: 115%
  }

  &__subtitle {
    a {
      border-bottom 1px solid
    }
  }

  &__footer {
    background: var(--gray);
    border: 1px solid var(--gray);
    border-radius: 0 0 var(--main_radius) var(--main_radius)
    text-align center
    padding: 30px
    display flex
    flex-direction column
    gap: 15px
    align-items center

    &-title {
      font-weight: 700;
      font-size: 1.125em
      line-height: 140%;
    }

    &--payment {
      background: var(--main-color-10, #fff8f0);
      border-color: var(--main-color-25, #ffd599);
    }
  }

  &__pay-notice {
    margin: 0;
    font-size: 0.9375rem;
    color: var(--main-color);
    line-height: 1.5;
  }

  &__pay-btn {
    min-width: 220px;
  }

  &__pay-error {
    color: var(--red, #e53935);
    font-size: 0.875rem;
  }
}
</style>
